import React, { useState } from 'react';
import { Card, FieldGroup, Grid, Link, Row, Stack, Text, TextInput } from 'library/ui';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';

import { useRequirementsAndPayContext } from '../context';
import { updateScale } from '../utils/scale';
import { AdditionalPills } from './AdditionalPills';
import { CredentialPill } from '../components/CredentialPill';
import { gatherDisplayPills } from '../utils/pills';

import type { GenericChoice, GenericQualificationChoices, IManagedScale, Pills, Qualification } from '../types';
import { MINIMUM_WAGE_BY_STATE } from '../constants';

export function BasePayRow({
    scale,
    qualificationChoices,
}: {
    scale: IManagedScale;
    qualificationChoices: GenericQualificationChoices;
}) {
    const { goToPrevious } = useJobFormContext();

    const { problems: allProblems, state, updateScales } = useRequirementsAndPayContext();
    const problems = allProblems[scale.id];

    const initialRate = isNaN(scale.rate) ? '0' : parseFloat(scale.rate.toFixed(2));

    const [rate, setRate] = useState<string>(initialRate.toString());

    const pills: Pills = gatherDisplayPills(scale, qualificationChoices, state);

    function update(
        value: number | number[],
        choices: GenericChoice[],
        qualification: Qualification | null,
        category: 'education' | 'experience' | 'multiple' | 'single' | 'boolean' | 'rate',
    ) {
        const newScale = updateScale(scale, value, choices, qualification, category);

        updateScales(newScale);
    }

    function coerceRate(rate: string): number {
        const parsedRate = parseFloat(rate);
        if (!isNaN(parsedRate)) {
            const coercedRate = parsedRate.toFixed(2);

            setRate(coercedRate);
            return parseFloat(coercedRate);
        }

        setRate(rate);
        return parsedRate;
    }

    return (
        <Card tone="muted" padding="sm">
            <Stack gap="none">
                <Grid template="minmax(0, 128px) minmax(0, 1fr)" collapseAt="md" gap="md" align="start">
                    <FieldGroup label="Pay rate">
                        <Row gap="sm" align="center">
                            <Text weight="bold">$</Text>
                            <TextInput
                                value={rate}
                                onChange={setRate}
                                onBlur={() => update(coerceRate(rate), [], null, 'rate')}
                                fullWidth
                                color="white"
                            />
                            <Text tone="secondary">/hr</Text>
                        </Row>
                    </FieldGroup>
                    <Row justify="between" align="start" gap="md" wrap>
                        <Stack gap="sm">
                            <Text weight="bold">Minimum Requirements</Text>
                            <Row gap="sm" wrap>
                                {pills.education?.value && <CredentialPill label={pills.education.value} selected />}
                                {pills.experience?.value && <CredentialPill label={pills.experience.value} selected />}
                                <AdditionalPills pills={pills} />
                            </Row>
                        </Stack>
                        <Link onClick={goToPrevious} size="sm">
                            Edit
                        </Link>
                    </Row>
                </Grid>
                {problems?.rate === 'toolow' && (
                    <Text size="xs" tone="error">
                        The lowest pay allowed is ${MINIMUM_WAGE_BY_STATE[state as keyof typeof MINIMUM_WAGE_BY_STATE]}
                    </Text>
                )}
                {problems?.rate === 'bad' && (
                    <Text size="xs" tone="error">
                        Please input a valid rate
                    </Text>
                )}
            </Stack>
        </Card>
    );
}
